import { ArrowLeft, KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import {
  SecurityPinSetupActions,
  SecurityPinSetupBody,
  SecurityPinStepper,
} from "@/shared/components/security/SecurityPinSetup";
import { StepUpMethodComparison } from "@/shared/components/security/StepUpMethodComparison";
import { useSecurityPinSetup } from "@/shared/components/security/useSecurityPinSetup";
import useTranslations from "@/shared/lang/useTranslations";
import { canUseSecurityPin } from "@/shared/utils/stepUp";

/** Where two-factor enrolment lives — `SecuritySection` of the settings page. */
export const TWO_FACTOR_SETTINGS_PATH = "/app/settings?section=seguridad";

/** How long the success state stays before the pending action's challenge opens on its own. */
const CONTINUE_AFTER_MS = 1400;

export interface TwoFactorRequiredModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Translated sentence naming the action that needs two-factor authentication. */
  description: string;
  onGoToSettings: () => void;
  /**
   * Runs once the person created a security PIN right here. Pass it to carry on with the action that asked
   * for the step-up (open its challenge); without it the notice just closes on success.
   */
  onEnrolled?: () => void;
}

/**
 * Shown instead of the challenge when the signed-in user has no step-up method: names the action that
 * requires one and offers the two ways to get it. An organization member chooses between the authenticator
 * app — the recommended, more secure option, set up in Settings — and a security PIN, created here in a
 * minute so the action can go on. INTERNAL staff, who cannot use a PIN, are only sent to enrol the
 * authenticator. Opening the challenge anyway would end in a 403 and a redirect away from the screen they
 * were working on.
 *
 * @summary enrolment notice for step-up actions: authenticator (recommended) or security PIN (fastest)
 */
export function TwoFactorRequiredModal(props: TwoFactorRequiredModalProps) {
  const { user } = useAuth();
  if (!props.open) return null;
  return canUseSecurityPin(user) ? <MemberEnrolment {...props} /> : <AuthenticatorOnlyNotice {...props} />;
}

function AuthenticatorOnlyNotice({
  open,
  onOpenChange,
  description,
  onGoToSettings,
}: TwoFactorRequiredModalProps) {
  const { t } = useTranslations("global");

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
          {t("two_factor_required_modal_title")}
        </span>
      }
      description={description}
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" />}>
            {t("two_factor_required_modal_cancel")}
          </ColocaDialogClose>
          <ColocaButton variant="primary" onClick={onGoToSettings}>
            {t("two_factor_required_modal_go_to_settings")}
          </ColocaButton>
        </>
      }
    />
  );
}

function MemberEnrolment({
  onOpenChange,
  description,
  onGoToSettings,
  onEnrolled,
}: TwoFactorRequiredModalProps) {
  const { t } = useTranslations("global");
  const [phase, setPhase] = useState<"choose" | "pin">("choose");
  const pin = useSecurityPinSetup();
  const done = pin.step === "done";

  const finish = () => {
    if (onEnrolled) onEnrolled();
    else onOpenChange(false);
  };

  // The success state is seen, then the pending action's challenge opens by itself: the PIN was created to go on.
  useEffect(() => {
    if (!done || !onEnrolled) return;
    const id = window.setTimeout(onEnrolled, CONTINUE_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [done, onEnrolled]);

  if (phase === "choose") {
    return (
      <ColocaDialog
        open
        onOpenChange={onOpenChange}
        size="lg"
        title={
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            {t("step_up_required_title")}
          </span>
        }
        description={description}
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <ColocaDialogClose
              render={<ColocaButton variant="ghost" className="w-full sm:mr-auto sm:w-auto" />}
            >
              {t("two_factor_required_modal_cancel")}
            </ColocaDialogClose>
            {/* The authenticator is the default — the recommended, stronger method — so it is the one primary
                action; the PIN stays one click away as the faster alternative. */}
            <ColocaButton
              variant="secondary"
              icon={KeyRound}
              onClick={() => setPhase("pin")}
              className="w-full sm:w-auto"
            >
              {t("step_up_choice_create_pin")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              icon={Smartphone}
              onClick={onGoToSettings}
              className="w-full sm:w-auto"
            >
              {t("step_up_choice_go_totp")}
            </ColocaButton>
          </div>
        }
      >
        <StepUpMethodComparison />
      </ColocaDialog>
    );
  }

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && pin.saving) return;
        if (!next && done) return finish();
        onOpenChange(next);
      }}
      showCloseButton={!pin.saving}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
          {t("security_pin_dialog_create_title")}
        </span>
      }
      description={done ? undefined : t("security_pin_dialog_description")}
      toolbar={done ? undefined : <SecurityPinStepper step={pin.step} />}
      footer={
        <SecurityPinSetupActions
          controller={pin}
          submitLabel={
            onEnrolled ? t("security_pin_submit_create_continue") : t("security_pin_submit_create")
          }
          done={{
            label: onEnrolled ? t("security_pin_done_continue") : t("security_pin_done_close"),
            onClick: finish,
          }}
          leading={
            <ColocaButton variant="ghost" icon={ArrowLeft} onClick={() => setPhase("choose")}>
              {t("security_pin_back")}
            </ColocaButton>
          }
        />
      }
    >
      <SecurityPinSetupBody
        controller={pin}
        doneBody={onEnrolled ? t("security_pin_done_body_continue") : undefined}
      />
    </ColocaDialog>
  );
}
